/**
 * ProductContentReview
 *
 * Product content review dashboard (pure code move from ContentReviewDashboard).
 * Shows metrics, filterable queue, and review panel for product content.
 *
 * Part of Story 29: Content Review Dashboard with Reviewer Attribution
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import {
  Filter,
  RefreshCw,
  FileText,
  Tag,
  FolderTree,
  Calendar,
  CheckSquare,
  CheckCircle,
  Square,
  Loader2,
  Search,
  X,
  Archive
} from 'lucide-react';
import { format, subDays, startOfDay, endOfDay, startOfYear } from 'date-fns';
import { useSite } from '@/contexts/SiteContext';
import { ReviewMetrics, TopReviewers } from '@/components/content-review/ReviewMetrics';
import { ContentQueue } from '@/components/content-review/ContentQueue';
import { bulkSelectionScopeNotice, selectionScopeSignature, nextSelectionScope, isSelectionCurrent, NO_SELECTION, nextSelectionSite, crossSiteRefusal, staleResponse, requestSignature, isLatestRequest, NO_SELECTION_SITE, type SelectionScope, type SelectionSite } from './bulkSelectionScope';
import { ReviewPanel } from '@/components/content-review/ReviewPanel';
import { FACET_SELECTS, facetSelectValue, facetFromSelect, activeFacets, facetsAreActive, facetBadges, labelWithCount, type FacetDimension } from './facetFilters';
import { useFacetCounts } from './useFacetCounts';
import {
  getMetrics,
  getQueue,
  getQueueIds,
  getContentDetails,
  bulkApprove,
  bulkRegenerate,
  getRealtimeRegenJob,
  onPollSuccess,
  onPollError,
  MAX_POLL_FAILURES,
  overrideReleasesGuard,
  type PollState,
  type PollDecision,
  getProductCategories,
  ReviewMetrics as MetricsData,
  QueueItem,
  QueueFilters,
  CategoryWithCount,
  BulkRegenerateResult,
  getActiveRealtimeRegen,
  RealtimeRegenAlreadyActiveError,
  REGEN_OUTCOME_LABELS,
  type RegenOutcome,
} from '@/services/contentReviewService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const CONTENT_TYPES = [
  { value: 'all', label: 'All Types', icon: null },
  { value: 'ai_article', label: 'AI Articles', icon: FileText },
  { value: 'product_aeo', label: 'Product AEO', icon: Tag },
  { value: 'category_aeo', label: 'Category AEO', icon: FolderTree },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'needs_research', label: 'Needs Research' },
];

const FAQ_QA_OPTIONS = [
  { value: 'all', label: 'All FAQ Status' },
  { value: 'passed', label: 'FAQ QA Passed' },
  { value: 'failed', label: 'FAQ QA Failed' },
];

const PREVIOUS_REJECTION_OPTIONS = [
  { value: 'all', label: 'All Items' },
  { value: 'with_rejection', label: 'Previously Rejected' },
  { value: 'without_rejection', label: 'No Previous Rejection' },
];

const MANUAL_REVIEW_OPTIONS = [
  { value: 'all', label: 'All Items' },
  { value: 'needs_review', label: 'Needs Manual Review' },
  { value: 'no_review', label: 'No Review Needed' },
];

const DATE_PRESET_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'this_year', label: 'This year' },
  { value: 'all_time', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

// Helper to get date range from preset
function getDateRangeFromPreset(preset: string): { from: Date; to: Date } | null {
  const now = new Date();
  switch (preset) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'yesterday':
      const yesterday = subDays(now, 1);
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
    case 'last_7_days':
      return { from: startOfDay(subDays(now, 7)), to: endOfDay(now) };
    case 'last_30_days':
      return { from: startOfDay(subDays(now, 30)), to: endOfDay(now) };
    case 'this_year':
      return { from: startOfYear(now), to: endOfDay(now) };
    case 'all_time':
      return null; // No date filter
    default:
      return null;
  }
}

export function ProductContentReview() {
  const { selectedSite } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();

  // Metrics state
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  // Queue state
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 25
  });

  // Filter state
  const [filters, setFilters] = useState<QueueFilters>({
    siteId: selectedSite?.id || null,
    contentType: null,
    status: 'pending',
    startDate: subDays(new Date(), 30).toISOString(),
    endDate: endOfDay(new Date()).toISOString(),
    page: 1,
    limit: 25,
    sortBy: 'updated_at',
    sortOrder: 'DESC'
  });

  // Review panel state
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null);
  const [reviewPanelOpen, setReviewPanelOpen] = useState(false);

  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  // The filter scope the CURRENT selection was made under. `selectedItems` survives filter and site
  // changes (the [filters] effect resets only `allItemsSelected`), so the scope badge must not
  // compare a stale selection against a fresh total. Stamped wherever a selection is created.
  const [selectionScope, setSelectionScope] = useState<SelectionScope>(NO_SELECTION);
  // The site the current selection belongs to. Kept ALONGSIDE the selection rather than clearing it
  // on a site switch, so a context change never silently discards an operator's work (orchestrator,
  // 14:34Z). It only ever gates EXECUTION — see crossSiteRefusal.
  const [selectionSite, setSelectionSite] = useState<SelectionSite>(NO_SELECTION_SITE);
  // ⛔⛔ THE LIVE SITE, READ AT RESPONSE TIME. An async handler closes over the site it was STARTED
  // on, which is what makes the request's own scope available — but it also means the handler cannot
  // see that the site has since moved. A ref is the only thing in that closure that can.
  const liveSiteIdRef = useRef<string | null>(selectedSite?.id || null);
  // ⛔⛔ AND THE LIVE FILTER SIGNATURE, for the loads whose scope is the FILTER SET rather than the
  // site. loadQueue is the case codex-reviewer found: a slow site-A queue response landing after a
  // completed site-B one paints A's ROWS while selectedSite says B, so the operator selects what
  // they can see and it is stamped B. Nothing downstream can catch that — the displayed data is the
  // corrupted input, and the guard is never given anything to object to.
  // ⛔ ONE GENERATION COUNTER PER ASYNC OPERATION THAT OWNS A SPINNER. See isLatestRequest: being
  // OUT OF SCOPE and being SUPERSEDED are different, and only the latter means someone else will
  // clear the flag on your behalf.
  const queueGenRef = useRef(0);
  const metricsGenRef = useRef(0);
  const categoriesGenRef = useRef(0);
  const selectAllGenRef = useRef(0);
  const itemGenRef = useRef(0);   // the last load without one; the grid had a hole rather than an exemption
  // ⛔⛔ EVERY SELECTION MUTATION BUMPS THIS. codex-reviewer at 1b008a0db: an in-flight select-all
  // could land after the operator cleared or changed the selection, restore it, and announce "All
  // Items Selected". Scope guards cannot see that — the site and the filters never moved; what
  // moved was the SELECTION, and nothing was tracking it.
  const selectionRevRef = useRef(0);
  const liveFilterSigRef = useRef<string>(requestSignature(filters));
  const liveSelectionSigRef = useRef<string>(selectionScopeSignature(filters));

  // ⛔⛔ ASSIGNED DURING RENDER, NOT IN AN EFFECT — AND THAT IS THE WHOLE FIX FOR ROUND 9.
  // codex-reviewer [blocking] at fe2a2c8f0: an effect runs AFTER the render that observed the
  // change, so between `selectedSite` becoming B and the effect firing, these refs still said A.
  // A response landing in that window passed the guard and painted A's rows under site B — the
  // exact corruption the guards exist to prevent, reintroduced by the guard's own update lag.
  // ⇒ COMMIT PHASE — useLayoutEffect. NOT during render, and NOT useEffect. Both neighbours are
  // wrong in OPPOSITE directions and I have now shipped one of each:
  //   · useEffect (round 8)  runs AFTER PAINT, in a later task, so a response resolving between
  //     commit and the effect compared against a ref that still named the previous scope. Late.
  //   · during render (round 9) escapes even from renders React ABANDONS under concurrent
  //     rendering, so the committed UI can show site A while the refs already hold an abandoned
  //     B — and a perfectly valid A response is then discarded with no replacement request
  //     coming, leaving stale rows on screen. Early, and unsafe. (codex-reviewer at 9017f12d8.)
  //
  // 🔑 THE COMPARAND MUST TRACK WHAT IS COMMITTED — what the operator can actually SEE — because
  // that is what "is this response still relevant" means. Not what a render was attempting.
  // useLayoutEffect runs synchronously in the commit phase, so it fires only for renders that
  // COMMITTED, and no microtask can interleave between the commit and the assignment.
  //
  // ⚠️ THREE REFS, THREE QUESTIONS, and they are NOT interchangeable:
  //   liveSiteIdRef      — the tenant. Anything that could execute against the wrong site.
  //   liveFilterSigRef   — the full filter set INCLUDING page: what the queue is painting.
  //   liveSelectionSigRef— the filter set EXCLUDING presentation: what a selection means.
  useLayoutEffect(() => {
    liveSiteIdRef.current = selectedSite?.id || null;
    liveFilterSigRef.current = requestSignature(filters);
    liveSelectionSigRef.current = selectionScopeSignature(filters);
  }, [selectedSite?.id, filters]);
  const [allItemsSelected, setAllItemsSelected] = useState(false); // Select all across pages
  const [loadingAllIds, setLoadingAllIds] = useState(false);
  const [bulkApproveLoading, setBulkApproveLoading] = useState(false);
  const [bulkRegenerateLoading, setBulkRegenerateLoading] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerateMode, setRegenerateMode] = useState<'batch' | 'realtime'>('batch');
  // Real-time regeneration is a 202 + background job (see contentReviewService.BulkRegenerateRealtimeResult).
  // These three drive the progress bar; jobId !== null is what "a run is being watched" means.
  const [regenerateJobId, setRegenerateJobId] = useState<string | null>(null);
  // ⛔⛔ THE RUN GUARD IS A *DIFFERENT* PIECE OF STATE FROM THE POLL HANDLE — codex-reviewer.
  // I originally derived "a run is active" from regenerateJobId. But regenerateJobId is cleared
  // whenever TRACKING stops — on 5 poll failures, or on stall detection — and both of those mean
  // "we can no longer observe the run", NOT "the run finished". Deriving the guard from it
  // therefore RE-ENABLED the button while the backend was probably still generating, so lost
  // tracking would have bought a duplicate paid cohort. My own fix created that.
  // ⇒ THE GUARD FOLLOWS THE RUN; THE HANDLE FOLLOWS THE POLLING. It is released ONLY on a
  //   confirmed terminal state from the backend, or by an explicit operator override.
  const [unconfirmedRunId, setUnconfirmedRunId] = useState<string | null>(null);
  const [regenerateProgress, setRegenerateProgress] = useState<{ percent: number; message: string }>({ percent: 0, message: '' });
  // ⛔ THE OUTCOME CARRIES THE RUN IT DESCRIBES. Without runId there is no way to tell whether the
  // card on screen belongs to the run currently in flight or to a finished one, which is what let
  // the override button release a guard it was never showing (codex-reviewer).
  const [regenerateOutcome, setRegenerateOutcome] = useState<{
    runId: string | null;
    message: string;
    details: Array<{ productId: string; status: string; error?: string; reason?: string }>;
  } | null>(null);

  // ⛔⛔ REHYDRATE ON LOAD — THE SERVER-SIDE GUARD'S OTHER HALF (codex-reviewer).
  // A reload, a second tab, or a browser crash all wipe the state above, and the run in the backend
  // does not care: it keeps generating and keeps costing money. Without this, that dashboard shows
  // an idle screen while a cohort runs, and the first thing the operator does is press the button —
  // which the server now refuses. Refusing without SHOWING is only half a fix, so on mount (and on
  // every site change) we ask the ledger what is actually running and re-attach to it.
  // ⚠️ It sets the SAME two pieces of state a fresh press does — the guard AND the poll handle — so
  // a rehydrated run is guarded exactly like one this tab started. Anything less and the second tab
  // would still be able to launch a duplicate the moment its own poll blipped.
  useEffect(() => {
    if (!selectedSite?.id) return;
    let cancelled = false;

    // ⛔⛔ DETACH BEFORE LOOKING UP — codex-reviewer, and my rehydrate effect caused it. The lookup
    // only ADOPTED a run; it never released one. So switching from site A (running) to site B (idle)
    // left A's job id and A's guard in place: the Regenerate button stayed disabled on B, and the
    // progress bar on screen described a run belonging to a site the operator was no longer looking
    // at. A guard held for the wrong site is indistinguishable, from the operator's seat, from a
    // broken button.
    // ⚠️ SAFE TO RELEASE THE GUARD HERE precisely because the server now owns it: the ledger is
    // authoritative, the lookup below re-attaches whatever is genuinely running for the new site,
    // and a press that races this cannot double-spend — it 409s and attaches.
    setRegenerateJobId(null);
    setUnconfirmedRunId(null);
    setRegenerateOutcome(null);
    setRegenerateProgress({ percent: 0, message: '' });

    (async () => {
      try {
        const active = await getActiveRealtimeRegen(selectedSite.id);
        if (cancelled || !active.active) return;
        setRegenerateJobId(active.jobId);
        setUnconfirmedRunId(active.jobId);
        setRegenerateOutcome(null);  // the previous run's card must not outlive the run it describes
        setRegenerateProgress(active.progress);
        toast({
          title: 'Reattached to a regeneration in progress',
          description: 'A real-time regeneration for this site was already running. Its progress is '
            + 'shown below.',
        });
      } catch (err) {
        // ⛔ LOG IT. This lookup failing means the dashboard may be blind to a live run and will let
        // the operator press into a 409 — recoverable (the 409 itself re-attaches), but silence here
        // would make that look like a random refusal with no trace of why.
        console.error('[ContentReviewDashboard] Active real-time regen lookup failed:', err);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite?.id]);

  // ⛔⛔ THE POLLING LOOP — THE 504 FIX'S OTHER HALF.
  // The request now returns a 202 handle, so completion arrives HERE, not from the POST.
  // ⚠️ A POLL FAILURE IS NOT A RUN FAILURE. The cohort keeps generating in the backend whether or
  // not this fetch succeeded, so a transient error must NOT stop the interval or report failure —
  // that would reproduce the original bug (a transport problem reported as a content problem).
  // Terminal states stop the loop; nothing else does.
  useEffect(() => {
    if (!regenerateJobId) return;
    // Pinned so every outcome this effect writes names the run THIS effect was watching, even
    // if state has since moved on. An outcome that names the wrong run is what scoped the override
    // to the wrong guard.
    const watchedJobId = regenerateJobId;
    // The poll is site-authorised, so the site is part of the handle. Pinned alongside the job id
    // so a site switch mid-flight cannot make this effect poll site B for site A's run.
    const watchedSiteId = selectedSite?.id;
    if (!watchedSiteId) return;
    // ⛔⛔ THE DECISION LOGIC LIVES IN contentReviewService (onPollSuccess / onPollError) AND IS
    // TESTED THERE. This effect is deliberately a thin adapter: poll, hand the result to the pure
    // decision, apply it. If the branching lived here too it would be untestable in practice — this
    // component needs SiteContext, a router, toasts and a dozen services to render — and a test that
    // re-implemented the same branching would certify the mirror, not the artefact.
    let cancelled = false;
    let state: PollState = { failures: 0, signature: '', lastChangeAt: Date.now() };

    const apply = (decision: PollDecision) => {
      if (decision.kind === 'continue') {
        setRegenerateProgress(decision.progress);
        return;
      }
      // Every non-continue decision STOPS the polling. ⚠️ Only the two CONFIRMED terminal states
      // also release the duplicate-run guard — tracking loss must not, because the cohort is
      // probably still generating and re-enabling the button would buy a second paid run.
      setRegenerateJobId(null);
      if (decision.kind === 'completed') {
        setRegenerateProgress(decision.progress);
        setRegenerateOutcome({
          runId: watchedJobId,
          message: decision.result?.message || 'Regeneration finished',
          details: decision.result?.details || [],
        });
        setUnconfirmedRunId(null);
        handleRefresh();
      } else if (decision.kind === 'aborted') {
        setRegenerateOutcome({ runId: watchedJobId, message: `Regeneration aborted: ${decision.error}`, details: [] });
        setUnconfirmedRunId(null);
      } else {
        // TRACKING lost — never "regeneration failed". We do not know the cohort died; we know we
        // can no longer observe it. Saying more would invent a terminal state the backend never wrote.
        setRegenerateOutcome({
          runId: watchedJobId,
          message: decision.reason === 'stalled'
            ? 'Lost track of this run — no progress for 5 minutes. The backend may have restarted '
              + 'mid-cohort; some products may have regenerated. Check the queue before re-running.'
            : `Lost track of this run after ${MAX_POLL_FAILURES} failed status checks — regeneration `
              + 'is probably still running in the background. Reloading this page will re-attach to '
              + 'it if it is still going.',
          details: [],
        });
      }
    };

    const tick = async () => {
      try {
        const job = await getRealtimeRegenJob(watchedSiteId, regenerateJobId);
        if (cancelled) return;
        const next = onPollSuccess(job, state, Date.now());
        state = next.state;
        apply(next.decision);
      } catch {
        if (cancelled) return;
        const next = onPollError(state);
        state = next.state;
        if (next.decision.kind !== 'continue') apply(next.decision);
      }
    };
    // ⛔⛔ SERIALISED, NOT setInterval-OVER-A-SLOW-REQUEST (codex-reviewer). A bare interval lets
    // requests overlap, and overlapping polls REORDER: a slow 'processing' response arriving after
    // a terminal one reverts the bar and un-finishes a finished run, and several overlapping
    // failures can replace a CONFIRMED outcome with "tracking lost". Both present as the UI
    // contradicting itself, which is the same class of lie as the 504 — the transport's timing
    // reported as the run's state.
    // ⇒ The next tick is scheduled only after the current one settles, so at most one request is
    //   ever in flight and responses cannot arrive out of order.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const loop = async () => {
      await tick();
      if (cancelled) return;
      timer = setTimeout(loop, 3000);
    };
    loop();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [regenerateJobId]);

  // S3: the backend has accepted these since 2026-05-12; the dialog never sent them.
  const [forceReEnrich, setForceReEnrich] = useState(false);
  const [forceRescore, setForceRescore] = useState(false);
  // task-fzfuulrr28: what happens to the regenerated pages when the run completes. Default = today's
  // behaviour (needs_review, approved by hand). Sent as chosen; the server 400s an unknown value.
  const [regenOutcome, setRegenOutcome] = useState<RegenOutcome>('leave_needs_review');
  const [faqQaFilter, setFaqQaFilter] = useState<string>('all');
  // task-8xq63qswed: the facet counts under the current filters (GET /queue/facets). Loaded with the queue; null
  // until the first load, so the selects render plain labels rather than a fabricated zero.
  // task-8xq63qswed: the facet counts follow the filters exactly; a failed fetch is visible, with a retry (round 2).
  const { counts: facetCounts, error: facetCountsError, retry: retryFacetCounts, refresh: refreshFacetCounts } = useFacetCounts(filters);
  const [previousRejectionFilter, setPreviousRejectionFilter] = useState<string>('all');
  const [manualReviewFilter, setManualReviewFilter] = useState<string>('all');
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [urlInput, setUrlInput] = useState<string>('');
  const [urlDebounceTimer, setUrlDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [categories, setCategories] = useState<CategoryWithCount[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false); // Show products no longer in active feed
  const [showPublished, setShowPublished] = useState(false); // Show published content

  // Date range state for filter
  const [datePreset, setDatePreset] = useState<string>('last_30_days');
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: startOfDay(subDays(new Date(), 30)),
    to: endOfDay(new Date())
  });

  // Handle URL params for direct linking (only when navigating directly, not from queue)
  useEffect(() => {
    const id = searchParams.get('id');
    const type = searchParams.get('type');

    // Only load from URL if we don't already have a selected item
    // (prevents overwriting when clicking Review from queue)
    if (id && type && !selectedItem) {
      loadItemFromUrl(type, id);
    }
  }, [searchParams]);

  // Update site filter when site changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      siteId: selectedSite?.id || null,
      category: null // Reset category filter when site changes
    }));
    setCategoryFilter('all');
    // ⛔ THE SELECTION IS DELIBERATELY *NOT* CLEARED HERE — see the cross-site refusal on the bulk
    // actions instead. Clearing was my first fix for codex-reviewer-2's blocking finding and it was
    // the blunt version: it silently discards an operator's selection on a context change, which is
    // the same product question as task-ytk1uncdwy and is Jaume's to settle, not something to decide
    // inside a blocking fix. Refusing to ACT across sites is strictly narrower — it does not throw
    // the selection away, it declines to use it somewhere it does not belong. (orchestrator, 14:34Z)
  }, [selectedSite?.id]);

  // Load categories when site changes
  useEffect(() => {
    const loadCategories = async () => {
      if (!selectedSite?.id) {
        setCategories([]);
        return;
      }
      const requestedSiteId = selectedSite?.id || null;
      // ⛔⛔ A SHARED handler that is actually CORRECT. codex-reviewer [blocking] at a93ddcdbb: the
      // payload-failure else I added last round called setCategories([]) with no toast AND NO LOG,
      // so a backend failure was indistinguishable from "this site has no categories" and the
      // operator filtered against a silently empty list. That is a silent fallback introduced BY
      // the fix for the silent-fallback axis.
      // 🔑 Routing both exits into one handler is right; it propagates whatever that handler does,
      // INCLUDING its defects. "Two branches that must stay identical will not" is an argument for
      // sharing something CORRECT, not for sharing.
      const failCategoriesLoad = (detail: unknown) => {
        console.error('[ContentReviewDashboard] Error loading categories:', detail);
        setCategories([]);
        toast({
          title: 'Error',
          description: 'Failed to load categories — the filter list may be incomplete.',
          variant: 'destructive',
        });
      };
      setCategoriesLoading(true);
      const gen = ++categoriesGenRef.current;
      try {
        const response = await getProductCategories(selectedSite.id);
      if (!isLatestRequest(gen, categoriesGenRef.current)) return; // ⛔ superseded, not merely out of scope
      if (staleResponse(requestedSiteId, liveSiteIdRef.current)) return; // ⛔ the response outlived its scope
        if (response.success) {
          setCategories(response.data);
        } else {
          failCategoriesLoad(response);
        }
      } catch (err) {
        if (!isLatestRequest(gen, categoriesGenRef.current)) return; // ⛔ superseded by a newer request
        if (staleResponse(requestedSiteId, liveSiteIdRef.current)) return; // ⛔ a late site-A FAILURE must not erase site-B's categories
        failCategoriesLoad(err);
      } finally {
        // ⛔ A STALE RESPONSE MUST NOT CLEAR THE LIVE REQUEST'S SPINNER. `return` still runs
        // `finally`, so an unguarded reset here would report "loaded" while the current request is
        // still in flight — the same class of lie, one layer down.
        if (isLatestRequest(gen, categoriesGenRef.current)) setCategoriesLoading(false);
      }
    };
    loadCategories();
  }, [selectedSite?.id]);

  // Load data when filters change
  useEffect(() => {
    loadMetrics();
    loadQueue();
    // Reset "all items selected" when filters change (the "all" now refers to different items)
    setAllItemsSelected(false);
  }, [filters]);

  const loadItemFromUrl = async (type: string, id: string) => {
    const requestedSiteId = selectedSite?.id || null;
    const gen = ++itemGenRef.current;
    const failItemLoad = () => toast({
      title: 'Error',
      description: 'Failed to load the requested content',
      variant: 'destructive',
    });
    try {
      // CRITICAL: Pass siteId for multi-tenancy security
      if (!selectedSite?.id) {
        console.warn('[ContentReviewDashboard] No site selected, cannot load item');
        return;
      }
      const response = await getContentDetails(type, id, selectedSite.id);
      if (!isLatestRequest(gen, itemGenRef.current)) return; // ⛔ superseded: a newer deep-link won
      if (staleResponse(requestedSiteId, liveSiteIdRef.current)) return; // ⛔ the response outlived its scope
      if (response.success) {
        // Backend already normalizes status to 'approved'/'rejected'/'pending'
        // No need to re-transform
        const normalizedStatus = response.data.status === 'approved' || response.data.status === 'published' ? 'approved' :
                                 response.data.status === 'rejected' || response.data.status === 'archived' ? 'rejected' : 'pending';
        const item: QueueItem = {
          id: response.data.id,
          title: response.data.title,
          contentType: response.data.contentType,
          status: normalizedStatus,
          createdAt: response.data.createdAt,
          updatedAt: response.data.updatedAt || response.data.createdAt,
          siteId: response.data.siteId,
          siteName: response.data.siteName,
          approvedBy: response.data.approvedBy || null,
          reviewerName: response.data.reviewerName || null,
          approvedAt: response.data.approvedAt || null,
          rejectionReason: response.data.rejectionReason || null,
          faqQaPassed: null,
          faqCount: null,
          previousRejection: null,
          needsManualReview: false,
          category: response.data.metadata?.category as string || null
        };
        setSelectedItem(item);
        setReviewPanelOpen(true);
      } else {
        // Payload-level failure — the fifth and last of this class on this surface. Without it a
        // deep link to a missing item opened nothing and said nothing.
        failItemLoad();
      }
    } catch (err) {
      console.error('[ContentReviewDashboard] Error loading item from URL:', err);
      if (!isLatestRequest(gen, itemGenRef.current)) return; // ⛔ superseded: a newer request already answered
      // ⛔ A STALE FAILURE MUST NOT SPEAK FOR A CONTEXT THE OPERATOR HAS LEFT. The error is still
      // LOGGED above — nothing is swallowed — but a toast about site A raised while site B is on
      // screen describes a request the operator can neither see nor act on.
      if (staleResponse(requestedSiteId, liveSiteIdRef.current)) return;
      failItemLoad();
    }
  };

  const loadMetrics = async () => {
    // CRITICAL: Require siteId for multi-tenancy security
    if (!filters.siteId) {
      setMetrics(null);
      return;
    }

    const requestedSig = requestSignature(filters);
    // ⛔ THE SITE IS CHECKED SEPARATELY FROM THE SIGNATURE. `filters.siteId` is written by an
    // effect, so it still names site A for one render after the operator has moved to B — a
    // signature built from it agrees with itself and proves nothing about the tenant.
    // ⛔⛔ AND IT IS CAPTURED FROM `filters`, WHICH IS WHAT THE REQUEST ACTUALLY USES. Capturing
    // `selectedSite` here was the round-9 fix overshooting in a second way (codex-reviewer-2 at
    // 1b008a0db): the fetch goes out with filters.siteId = A while selectedSite already says B, so
    // the guard compared B against a live B, PASSED, and painted A's rows under site B.
    // 🔑 A REQUEST'S SCOPE IS WHAT IT WAS ISSUED WITH, NOT WHAT THE OPERATOR NOW INTENDS. Compare
    // the issued scope against the live one; do not conflate the two.
    const requestedSiteId = filters.siteId || null;
    const failMetricsLoad = () => toast({
      title: 'Error',
      description: 'Failed to load metrics',
      variant: 'destructive',
    });
    setMetricsLoading(true);
    const gen = ++metricsGenRef.current;

    try {
      const response = await getMetrics({
        siteId: filters.siteId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        // Same toggle loadQueue sends. Omitting it here is what made "Total Pending"
        // disagree with the rows below it.
        includeInactive: filters.includeInactive
      });
      // ⛔⛔ AND SUPERSESSION, NOT ONLY SCOPE. A1 -> visit B -> back to A -> A2 finishes -> A1
      // lands: every scope guard PASSES because A1's scope IS the current scope. Two refreshes
      // do it with no navigation. A signature says "same KIND of request"; only a generation
      // says "same request", and painting needs the second.
      if (!isLatestRequest(gen, metricsGenRef.current)) return;
      if (staleResponse(requestedSig, liveFilterSigRef.current)
          || staleResponse(requestedSiteId, liveSiteIdRef.current)) return; // ⛔ the response outlived its scope

      if (response.success) {
        setMetrics(response.data);
      } else {
        // Same payload-level failure as the queue. Mirrors this load's catch EXACTLY rather than
        // inventing new behaviour: the catch toasts and does not clear, so this does the same.
        // ⚠️ Stale metric NUMBERS therefore survive a payload failure — presentation, recoverable
        // by reload, and deliberately not changed here, because diverging the two exits is the
        // defect being fixed.
        failMetricsLoad();
      }
    } catch (err) {
      console.error('[ContentReviewDashboard] Error loading metrics:', err);
      if (!isLatestRequest(gen, metricsGenRef.current)) return; // ⛔ superseded: a newer request already answered
      // ⛔ A STALE FAILURE MUST NOT SPEAK FOR A CONTEXT THE OPERATOR HAS LEFT. The error is still
      // LOGGED above — nothing is swallowed — but a toast about site A raised while site B is on
      // screen describes a request the operator can neither see nor act on.
      if (staleResponse(requestedSig, liveFilterSigRef.current)
          || staleResponse(requestedSiteId, liveSiteIdRef.current)) return;
      failMetricsLoad();
    } finally {
      // ⛔ A STALE RESPONSE MUST NOT CLEAR THE LIVE REQUEST'S SPINNER. `return` still runs
      // `finally`, so an unguarded reset here would report "loaded" while the current request is
      // still in flight — the same class of lie, one layer down.
      if (isLatestRequest(gen, metricsGenRef.current)) setMetricsLoading(false);
    }
  };

  const loadQueue = async () => {
    const requestedSig = requestSignature(filters);
    // ⛔ THE SITE IS CHECKED SEPARATELY FROM THE SIGNATURE. `filters.siteId` is written by an
    // effect, so it still names site A for one render after the operator has moved to B — a
    // signature built from it agrees with itself and proves nothing about the tenant.
    // ⛔⛔ AND IT IS CAPTURED FROM `filters`, WHICH IS WHAT THE REQUEST ACTUALLY USES. Capturing
    // `selectedSite` here was the round-9 fix overshooting in a second way (codex-reviewer-2 at
    // 1b008a0db): the fetch goes out with filters.siteId = A while selectedSite already says B, so
    // the guard compared B against a live B, PASSED, and painted A's rows under site B.
    // 🔑 A REQUEST'S SCOPE IS WHAT IT WAS ISSUED WITH, NOT WHAT THE OPERATOR NOW INTENDS. Compare
    // the issued scope against the live one; do not conflate the two.
    const requestedSiteId = filters.siteId || null;
    // CRITICAL: Require siteId for multi-tenancy security
    if (!filters.siteId) {
      setQueue([]);
      setPagination({ page: 1, totalPages: 0, total: 0, limit: 25 });
      return;
    }

    setQueueLoading(true);
    const gen = ++queueGenRef.current;

    // ⛔⛔ ONE FAILURE HANDLER FOR BOTH EXITS — see the banner at the `else` below.
    const failQueueLoad = () => {
      setQueue([]);
      setPagination({ page: 1, totalPages: 0, total: 0, limit: 25 });
      toast({
        title: 'Error',
        description: 'Failed to load content queue',
        variant: 'destructive',
      });
    };

    try {
      const response = await getQueue(filters);
      // ⛔⛔ AND SUPERSESSION, NOT ONLY SCOPE. A1 -> visit B -> back to A -> A2 finishes -> A1
      // lands: every scope guard PASSES because A1's scope IS the current scope. Two refreshes
      // do it with no navigation. A signature says "same KIND of request"; only a generation
      // says "same request", and painting needs the second.
      if (!isLatestRequest(gen, queueGenRef.current)) return;
      if (staleResponse(requestedSig, liveFilterSigRef.current)
          || staleResponse(requestedSiteId, liveSiteIdRef.current)) return; // ⛔ the response outlived its scope

      if (response.success) {
        setQueue(response.data.items);
        setPagination({
          page: response.data.page,
          totalPages: response.data.totalPages,
          total: response.data.total,
          limit: response.data.limit
        });
      } else {
      // ⛔⛔ A 2xx THAT REPORTS FAILURE INSIDE ITS OWN PAYLOAD. codex-reviewer-2 [blocking] at
      // 94fd7821a. Every service here throws only on `!response.ok`, so an application-level
      // failure RESOLVES: nothing rejects, the catch never runs, and the guard passes because the
      // response genuinely is current-scope. The request succeeded and the operation failed, and
      // the two exits had one handler between them.
      // ⇒ ONE HANDLER, BOTH EXITS. Sharing it is the point — the round-8 lesson was that a load has
      // more ways out than the one you guarded, and two branches that must stay identical will not.
        failQueueLoad();
      }
    } catch (err) {
      console.error('[ContentReviewDashboard] Error loading queue:', err);
      if (!isLatestRequest(gen, queueGenRef.current)) return; // ⛔ superseded: a newer request already answered
      // ⛔ A STALE FAILURE MUST NOT SPEAK FOR A CONTEXT THE OPERATOR HAS LEFT. The error is still
      // LOGGED above — nothing is swallowed — but a toast about site A raised while site B is on
      // screen describes a request the operator can neither see nor act on.
      if (staleResponse(requestedSig, liveFilterSigRef.current)
          || staleResponse(requestedSiteId, liveSiteIdRef.current)) return;
      // ⛔⛔ AND THE ROWS MUST GO. codex-reviewer-2 [blocking] at 9017f12d8: a FAILED current-scope
      // load left the PREVIOUS filter's rows rendered, and handleSelectionChange stamps a clicked
      // row with the CURRENT scope — so selecting one produced a selection that looked current, and
      // the cross-site refusal had nothing to object to. After a same-site filter change those ids
      // RESOLVE, so a bulk approve changes customer-facing state for a cohort the operator never
      // selected under the filter they were looking at.
      // ⇒ Cleared rather than left visible-but-disabled: rows on screen under the wrong filter are
      // a claim about what matches it, and the toast above already says the load failed.
      failQueueLoad();
    } finally {
      // ⛔ A STALE RESPONSE MUST NOT CLEAR THE LIVE REQUEST'S SPINNER. `return` still runs
      // `finally`, so an unguarded reset here would report "loaded" while the current request is
      // still in flight — the same class of lie, one layer down.
      if (isLatestRequest(gen, queueGenRef.current)) setQueueLoading(false);
    }
  };

  const handleRefresh = () => {
    loadMetrics();
    loadQueue();
    refreshFacetCounts();   // the counts follow the queue on every refresh, not only on a filter change (round 3)
  };

  const handleContentTypeChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      contentType: value === 'all' ? null : value,
      page: 1
    }));
  };

  const handleStatusChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      status: value === 'all' ? null : value,
      page: 1
    }));
  };

  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset);

    if (preset === 'custom') {
      // Keep current date range when switching to custom
      return;
    }

    const range = getDateRangeFromPreset(preset);
    if (range) {
      setDateRange(range);
      setFilters(prev => ({
        ...prev,
        startDate: range.from.toISOString(),
        endDate: range.to.toISOString(),
        page: 1
      }));
    } else {
      // all_time - no date filter
      setFilters(prev => ({
        ...prev,
        startDate: null,
        endDate: null,
        page: 1
      }));
    }
  };

  const handleDateRangeChange = (range: { from: Date; to: Date }) => {
    setDateRange(range);
    setFilters(prev => ({
      ...prev,
      startDate: range.from.toISOString(),
      endDate: endOfDay(range.to).toISOString(),
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSortChange = (column: 'updated_at' | 'title' | 'content_type') => {
    setFilters(prev => {
      // If clicking the same column, toggle the order
      if (prev.sortBy === column) {
        return {
          ...prev,
          sortOrder: prev.sortOrder === 'ASC' ? 'DESC' : 'ASC',
          page: 1
        };
      }
      // New column - default to DESC for date, ASC for others
      return {
        ...prev,
        sortBy: column,
        sortOrder: column === 'updated_at' ? 'DESC' : 'ASC',
        page: 1
      };
    });
  };

  const handleReview = (item: QueueItem) => {
    setSelectedItem(item);
    setReviewPanelOpen(true);
    // Update URL for direct linking
    setSearchParams({ id: item.id, type: item.contentType });
  };

  const handleReviewPanelClose = () => {
    setReviewPanelOpen(false);
    setSelectedItem(null);
    // Clear URL params
    setSearchParams({});
  };

  const handleActionComplete = () => {
    // Refresh data after approve/reject
    loadMetrics();
    loadQueue();
  };

  const handleFaqQaFilterChange = (value: string) => {
    setFaqQaFilter(value);
    setFilters(prev => ({
      ...prev,
      faqQaPassed: value === 'passed' ? true : value === 'failed' ? false : null,
      page: 1
    }));
  };

  // task-8xq63qswed: a facet lives in `filters` itself (never in a state of its own), so the queue, Select All and
  // the counts serialise it through the one shared declaration. Changing a facet resets the page.
  const handleFacetChange = (key: FacetDimension, value: string) => {
    setFilters(prev => ({ ...prev, [key]: facetFromSelect(key, value), page: 1 }));
  };
  const handleApprovableToggle = () => {
    setFilters(prev => ({ ...prev, preset: prev.preset === 'approvable' ? null : 'approvable', page: 1 }));
  };
  const handleClearFacets = () => {
    setFilters(prev => ({ ...prev, validation: null, issue: null, gate: null, specRows: null, firstTime: null, preset: null, page: 1 }));
  };

  const handlePreviousRejectionFilterChange = (value: string) => {
    setPreviousRejectionFilter(value);
    setFilters(prev => ({
      ...prev,
      hasPreviousRejection: value === 'with_rejection' ? true : value === 'without_rejection' ? false : null,
      page: 1
    }));
  };

  const handleManualReviewFilterChange = (value: string) => {
    setManualReviewFilter(value);
    setFilters(prev => ({
      ...prev,
      needsManualReview: value === 'needs_review' ? true : value === 'no_review' ? false : null,
      page: 1
    }));
  };

  const handleCategoryFilterChange = (value: string) => {
    setCategoryFilter(value);
    setFilters(prev => ({
      ...prev,
      category: value === 'all' ? null : value,
      page: 1
    }));
  };

  const handleShowInactiveChange = (checked: boolean) => {
    setShowInactive(checked);
    setFilters(prev => ({
      ...prev,
      includeInactive: checked,
      page: 1
    }));
  };

  const handleShowPublishedChange = (checked: boolean) => {
    setShowPublished(checked);
    setFilters(prev => ({
      ...prev,
      includePublished: checked,
      page: 1
    }));
  };

  // Helper to get the last segment of a category path (e.g., "Bikes > Road > Racing" → "Racing")
  const getCategoryShortName = (categoryPath: string): string => {
    const parts = categoryPath.split(' > ');
    return parts[parts.length - 1] || categoryPath;
  };

  // Select all items on current page that don't need manual review (safe for bulk approval)
  const handleSelectAllSafe = () => {
    const safeItems = queue
      .filter(item => !item.needsManualReview && item.status === 'pending')
      .map(item => `${item.contentType}:${item.id}`);
    selectionRevRef.current += 1;
    setSelectedItems(new Set(safeItems));
    setSelectionScope({ kind: 'current', signature: selectionScopeSignature(filters) });
    // ⛔ THE ROWS' SITE, NOT THE SELECTOR'S. These rows came from `filters`; stamping them with
    // `selectedSite` during a transition claims a provenance they do not have, and crossSiteRefusal
    // then has nothing to object to because the stamp already agrees with the current site.
    setSelectionSite({ kind: 'single', siteId: filters.siteId || null });
    setAllItemsSelected(false);
  };

  // Select ALL items across all pages matching current filters
  const selectAllItemsAcrossPages = async () => {
    if (!filters.siteId) return;

    // ⛔⛔ SCOPE THE RESPONSE, NOT THE MOMENT. Both codex legs [blocking] at 44dd530e7: a select-all
    // started on site A can RESOLVE after the operator has switched to B, install A's ids and stamp
    // A's scope while B is on screen. A guard that runs at the instant of the site change cannot see
    // a request that was already in flight when it ran — the guard is synchronous and the hazard is
    // asynchronous. So the request carries the site it was issued under, and the response is
    // discarded if that no longer matches. Structurally the same as reading a before-image at one
    // instant and using it to describe a row updated at another (#4794).
    const requestedSiteId = filters.siteId || null;   // ⛔ what getQueueIds will actually fetch with
    // ⛔ AND THE SELECTION SCOPE, NOT ONLY THE SITE. codex-reviewer-2 [blocking] at fe2a2c8f0:
    // start select-all under filter A, switch to filter B on the SAME site, make a new selection —
    // A's response still replaced it and announced "All Items Selected". Same site, so those ids
    // RESOLVE: approving them changes customer-facing state the operator never chose.
    // ⚠️ selectionScopeSignature, not requestSignature: a cross-page selection must survive paging,
    // which is exactly the distinction the two signatures exist to make.
    const requestedSelectionSig = selectionScopeSignature(filters);
    // ⛔ AND THE SELECTION ITSELF. "Clear Selection" stays enabled while this is in flight, and row
    // or header clicks are possible too — none of which move the site or the filters, so every
    // scope guard here is blind to them. Without this, a select-all landing after a clear RESTORES
    // the selection and announces "All Items Selected".
    const requestedSelectionRev = selectionRevRef.current;
    const failSelectAll = () => toast({
      title: 'Error',
      description: 'Failed to select all items.',
      variant: 'destructive',
    });

    setLoadingAllIds(true);
    const gen = ++selectAllGenRef.current;
    try {
      // ⛔⛔ PASS THE SAME OBJECT THE QUEUE IS FETCHED WITH — do NOT re-enumerate the filters here.
      // This used to be a hand-typed subset while the queue above got `filters` whole, so the DISPLAY
      // picked up every new filter automatically and the SELECTION picked up only what someone
      // remembered to type. The two therefore diverged BY DEFAULT and converged only by memory.
      // `includePublished` is what that cost: the queue showed published rows and "Select All"
      // silently left them out (task-u0qpjf2q4m, then task-gkula84yct for this class).
      // ⚠️ Pagination and sort ride along harmlessly — getQueueIds serialises the filter set through
      // the service's single shared declaration, which deliberately excludes them; a cross-page
      // selection has no page.
      const response = await getQueueIds(filters);

      const selectionMoved = selectionRevRef.current !== requestedSelectionRev;
      if (!isLatestRequest(gen, selectAllGenRef.current)) return; // ⛔ a newer select-all owns the outcome
      if (selectionMoved
          || staleResponse(requestedSiteId, liveSiteIdRef.current)
          || staleResponse(requestedSelectionSig, liveSelectionSigRef.current)) {
        // ⚠️ Announced, never silent. A select-all that quietly does nothing is indistinguishable
        // from one that failed, and the operator would reasonably click it again.
        // ⚠️ NAME THE ACTUAL CAUSE. This guard now fires for a site change OR a filter change, and a
        // message that says "site" when the filter moved describes a state the operator can see is
        // wrong — which teaches them to distrust the next one.
        const siteMoved = staleResponse(requestedSiteId, liveSiteIdRef.current);
        toast({
          title: selectionMoved ? 'Selection changed while selecting'
               : siteMoved ? 'Site changed while selecting'
               : 'Filters changed while selecting',
          description: selectionMoved
            ? 'Select All was discarded because the selection changed while it was loading. '
              + 'Your current selection was kept.'
            : siteMoved
            ? 'The selection was discarded because it was made on a different site. '
              + 'Select all again on this site if you still want it.'
            : 'The selection was discarded because the filters changed while it was loading. '
              + 'Select all again if you still want it.',
          variant: 'destructive',
        });
        return;
      }

      if (response.success) {
        const allIds = response.data.ids.map(item => `${item.contentType}:${item.id}`);
        setSelectedItems(new Set(allIds));
        setSelectionScope({ kind: 'current', signature: selectionScopeSignature(filters) });
        setSelectionSite({ kind: 'single', siteId: requestedSiteId });
        setAllItemsSelected(true);
        toast({
          title: 'All Items Selected',
          description: `Selected ${allIds.length} items across all pages.${response.data.truncated ? ' (limited to 10,000)' : ''}`,
        });
      } else {
        // Payload-level failure. Without this, "Select All" did NOTHING and said nothing — the
        // spinner cleared and the operator was left to conclude the click had not registered.
        failSelectAll();
      }
    } catch (error) {
      console.error('[ContentReviewDashboard] Error fetching all item IDs:', error);
      if (!isLatestRequest(gen, selectAllGenRef.current)) return; // ⛔ superseded: a newer request already answered
      // ⛔ A STALE FAILURE MUST NOT SPEAK FOR A CONTEXT THE OPERATOR HAS LEFT. The error is still
      // LOGGED above — nothing is swallowed — but a toast about site A raised while site B is on
      // screen describes a request the operator can neither see nor act on.
      // ⛔⛔ BOTH DIMENSIONS, MATCHING THE RESOLVE PATH. Found by enumerating over COMPARANDS rather
      // than positions: this cell was "guarded" in the 21-cell table and its guard compared only the
      // SITE, while the request depends on every scope-changing filter. A same-site filter change
      // therefore still raised "Failed to select all items" about a request the operator had left.
      // The position was right and the comparand was incomplete — which is precisely the gap a
      // position-based enumeration cannot see.
      if (staleResponse(requestedSiteId, liveSiteIdRef.current)
          || staleResponse(requestedSelectionSig, liveSelectionSigRef.current)) return;
      failSelectAll();
    } finally {
      // ⛔ A STALE RESPONSE MUST NOT CLEAR THE LIVE REQUEST'S SPINNER. `return` still runs
      // `finally`, so an unguarded reset here would report "loaded" while the current request is
      // still in flight — the same class of lie, one layer down.
      if (isLatestRequest(gen, selectAllGenRef.current)) setLoadingAllIds(false);
    }
  };

  // Clear all selections
  const clearSelection = () => {
    selectionRevRef.current += 1;
    setSelectedItems(new Set());
    setAllItemsSelected(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    // Debounce the search to avoid too many API calls
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    const timer = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        search: value.trim() || null,
        page: 1
      }));
    }, 300);
    setSearchDebounceTimer(timer);
  };

  const clearSearch = () => {
    setSearchInput('');
    setFilters(prev => ({
      ...prev,
      search: null,
      page: 1
    }));
  };

  const handleUrlContainsChange = (value: string) => {
    setUrlInput(value);
    // Debounce identically to the search field to avoid spam on every keystroke
    if (urlDebounceTimer) {
      clearTimeout(urlDebounceTimer);
    }
    const timer = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        urlContains: value.trim() || null,
        page: 1
      }));
    }, 300);
    setUrlDebounceTimer(timer);
  };

  const clearUrlContains = () => {
    setUrlInput('');
    setFilters(prev => ({
      ...prev,
      urlContains: null,
      page: 1
    }));
  };

  const handleSelectionChange = (newSelected: Set<string>, mode: 'replace' | 'toggle' = 'toggle') => {
    // ⛔ Do NOT stamp the current scope unconditionally: on a TOGGLE, ContentQueue copies the
    // existing selection and adds one id, so `newSelected` can carry rows chosen under a PREVIOUS
    // filter. Relabelling those as current is what made the badge undercount.
    // ⚠️ But a REPLACE carries nothing forward — the header checkbox discards the old set entirely
    // — so treating it as additive marked a wholly-current selection `mixed` and warned falsely.
    // Both codex legs found that independently. `hadSelection` means "did the incoming set carry
    // the previous one forward", which is exactly what `mode` now answers.
    const carriedForward = mode !== 'replace' && selectedItems.size > 0;
    setSelectionSite(prev => nextSelectionSite(
      carriedForward ? prev : NO_SELECTION_SITE,
      carriedForward,
      newSelected.size > 0,
      filters.siteId || null   // ⛔ the site the DISPLAYED rows came from, not the selector's
    ));
    setSelectionScope(prev => nextSelectionScope(
      carriedForward ? prev : NO_SELECTION,
      carriedForward,
      newSelected.size > 0,
      selectionScopeSignature(filters)
    ));
    selectionRevRef.current += 1;
    setSelectedItems(newSelected);
    // Reset "all items selected" when manually changing selection
    setAllItemsSelected(false);
  };

  const handleBulkApprove = async () => {
    if (selectedItems.size === 0) {
      toast({
        title: 'No items selected',
        description: 'Please select at least one item to approve',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedSite?.id) {
      toast({
        title: 'No site selected',
        description: 'Please select a site first',
        variant: 'destructive',
      });
      return;
    }

    // ⛔⛔ REFUSE CROSS-SITE EXECUTION. The ids are sent with the CURRENT site, so a selection built
    // on another site would be submitted against a site that does not own it. Declining is strictly
    // narrower than clearing the selection on a site switch: it does not throw the operator's work
    // away, it only refuses to act on it somewhere it does not belong.
    const refusal = crossSiteRefusal(selectionSite, selectedSite?.id || null);
    if (refusal) {
      toast({
        title: 'Selection belongs to a different site',
        description: refusal,
        variant: 'destructive',
      });
      return;
    }

    setBulkApproveLoading(true);
    try {
      // Convert Set to array of {contentType, id} objects
      const items = Array.from(selectedItems).map(key => {
        const [contentType, id] = key.split(':');
        return { contentType, id };
      });

      // task-8xq63qswed: the active facets are the press's exclusion — re-checked row by row at press time.
      const result = await bulkApprove(items, selectedSite.id, undefined, activeFacets(filters));

      if (result.success) {
        // `result.message` already reads "Approved X of Y items[; Z held pending an active
        // category split]" — the route builds it. What it never named is FAILURES: they were
        // only derivable as Y − X − Z, and the detail went to console.warn, which nobody has
        // open. Naming the count costs one expression and stops a partial batch reading as a
        // clean one. (task-i4szfth3i6)
        const refusedNote = (result.refused?.length || 0) > 0
          ? `; ${result.refused!.length} refused at press time (their rows no longer pass the active filter)`
          : '';
        toast({
          title: 'Bulk Approval Complete',
          description: (result.failed.length > 0
            ? `${result.message}; ${result.failed.length} failed`
            : result.message) + refusedNote,
        });
        selectionRevRef.current += 1;
        setSelectedItems(new Set());
        setAllItemsSelected(false);
        handleRefresh();
      } else {
        // ⛔⛔ A WRITE THAT REPORTS FAILURE IN ITS PAYLOAD, AND SAID NOTHING. codex-reviewer-2
        // [blocking] at f3a35c8d0; TAKEN under the house rule — no silent fallbacks, ever — rather
        // than under the correctness line, because this does not change customer state and does not
        // execute an unchosen action: it fails to execute one the operator DID choose, silently.
        // The spinner cleared and no toast appeared, so a consequential action looked like a click
        // that never registered, and the operator cannot tell those apart.
        // ⚠️ The payload's own message is used where present. A generic string here would replace
        // the server's account of what went wrong with our guess at it.
        toast({
          title: 'Bulk Approval Failed',
          description: result.message || 'The server rejected the bulk approval.',
          variant: 'destructive',
        });
      }

      if (result.failed.length > 0) {
        console.warn('[ContentReviewDashboard] Some items failed to approve:', result.failed);
      }
    } catch (err) {
      console.error('[ContentReviewDashboard] Bulk approve error:', err);
      toast({
        title: 'Error',
        description: 'Failed to bulk approve content',
        variant: 'destructive',
      });
    } finally {
      setBulkApproveLoading(false);
    }
  };

  // ⛔⛔ ONE REAL-TIME COHORT AT A TIME — codex [blocking].
  // `bulkRegenerateLoading` clears the moment the 202 comes back, so the button re-enabled while
  // the cohort was still generating. A second press would launch the SAME expensive cohort again
  // AND overwrite `regenerateJobId`, orphaning the first run's progress — the operator would watch
  // run #2 believing it was run #1, while run #1 kept generating unobserved.
  // ⚠️ The bar is deliberately NOT the only guard: it is rendered outside the dialog and could be
  // scrolled off, so the check lives at the ACTION, not in the UI.
  const realtimeRunActive = unconfirmedRunId !== null;

  const handleOpenRegenerateDialog = () => {
    if (selectedItems.size === 0) {
      toast({
        title: 'No items selected',
        description: 'Please select at least one item to regenerate',
        variant: 'destructive',
      });
      return;
    }

    // Count product_aeo items
    const productAeoCount = Array.from(selectedItems).filter(key =>
      key.startsWith('product_aeo:')
    ).length;

    if (productAeoCount === 0) {
      toast({
        title: 'No product AEO items selected',
        description: 'Bulk regeneration only supports product AEO items',
        variant: 'destructive',
      });
      return;
    }

    // Default to batch for > 10 items
    if (productAeoCount > 10) {
      setRegenerateMode('batch');
    }

    // ⛔ Reset force flags on every open. A sticky force is the kind of state that silently
    // re-enriches a cohort the operator only meant to regenerate.
    setForceReEnrich(false);
    setForceRescore(false);
    setRegenerateDialogOpen(true);
  };

  const handleBulkRegenerate = async () => {
    // Guarded at the action rather than only on the button: a stale click, a keyboard submit, or a
    // re-render race all reach here, and the cost of getting it wrong is a duplicate paid cohort.
    // ⛔ SCOPED TO REAL-TIME (both codex legs). This guard used to fire for BATCH too, so an active
    // real-time cohort removed batch regeneration — an otherwise supported capability — for up to
    // 45 minutes. The server slot only ever excluded another REAL-TIME cohort, so the UI was
    // enforcing a restriction the backend does not have: a client-side rule stricter than the real
    // one, which reads to the operator as a broken feature rather than as a deliberate hold.
    if (realtimeRunActive && regenerateMode === 'realtime') {
      toast({
        title: 'A real-time regeneration is already running',
        description: 'Wait for it to finish before starting another — starting a second run would '
          + 'regenerate the same products again and hide the first run\'s progress.',
        variant: 'destructive',
      });
      return;
    }
    if (!selectedSite?.id) {
      toast({
        title: 'No site selected',
        description: 'Please select a site first',
        variant: 'destructive',
      });
      return;
    }

    // ⛔⛔ REFUSE CROSS-SITE EXECUTION. The ids are sent with the CURRENT site, so a selection built
    // on another site would be submitted against a site that does not own it. Declining is strictly
    // narrower than clearing the selection on a site switch: it does not throw the operator's work
    // away, it only refuses to act on it somewhere it does not belong.
    const refusal = crossSiteRefusal(selectionSite, selectedSite?.id || null);
    if (refusal) {
      toast({
        title: 'Selection belongs to a different site',
        description: refusal,
        variant: 'destructive',
      });
      return;
    }

    setBulkRegenerateLoading(true);
    try {
      // Convert Set to array of {contentType, id} objects
      const items = Array.from(selectedItems).map(key => {
        const [contentType, id] = key.split(':');
        return { contentType, id };
      });

      const result = await bulkRegenerate(items, regenerateMode, selectedSite.id, { forceReEnrich, forceRescore, outcome: regenOutcome });

      if (result.success) {
        setRegenerateDialogOpen(false);
        selectionRevRef.current += 1;
        setSelectedItems(new Set());
        setAllItemsSelected(false);

        if (result.mode === 'batch') {
          toast({
            title: 'Batch Regeneration Started',
            description: `${result.totalProducts} products queued for regeneration. Check Feed Import Status for progress.`,
          });
        } else {
          // ⛔⛔ THIS BRANCH USED TO SAY "Regeneration Complete" THE INSTANT THE REQUEST RETURNED.
          // Real-time regeneration is ~90-120s PER PRODUCT and the response is a 202 HANDLE — the
          // work has not started when this runs. The old toast was wrong even before the proxy
          // timeout made it visible; the 504 only turned a false success into a false failure.
          // ⚠️ NOTE THE TYPE CHANGE DID NOT CATCH THIS. Removing regenerated/failed/details broke
          // nothing here, because this line only read `message`, which still exists on the handle.
          // The compiler protects the fields it removes, not the claim the copy makes.
          setRegenerateJobId(result.jobId);
          setUnconfirmedRunId(result.jobId);
          setRegenerateOutcome(null);  // the previous run's card must not outlive the run it describes
          setRegenerateProgress({ percent: 0, message: result.message });
          toast({
            title: 'Regeneration Started',
            description: `${result.totalProducts} product(s) queued. Progress is shown below — this takes ~2 minutes per product.`,
          });
        }
      } else {
        // ⛔⛔ A WRITE THAT REPORTS FAILURE IN ITS PAYLOAD, AND SAID NOTHING. codex-reviewer-2
        // [blocking] at f3a35c8d0; TAKEN under the house rule — no silent fallbacks, ever — rather
        // than under the correctness line, because this does not change customer state and does not
        // execute an unchosen action: it fails to execute one the operator DID choose, silently.
        // The spinner cleared and no toast appeared, so a consequential action looked like a click
        // that never registered, and the operator cannot tell those apart.
        // ⚠️ The payload's own message is used where present. A generic string here would replace
        // the server's account of what went wrong with our guess at it.
        toast({
          title: 'Regeneration Failed',
          description: result.message || 'The server rejected the regeneration request.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      // ⛔⛔ THE SERVER REFUSED BECAUSE A RUN IS ALREADY IN FLIGHT — ATTACH TO IT, DO NOT JUST SAY NO.
      // This is the case the local React guard cannot cover: a reload or a second tab has no memory
      // of the first run, so it presses, and the only thing that knows is the database. Showing a
      // bare error here would leave the operator refused AND blind, which is what makes someone
      // press again.
      if (err instanceof RealtimeRegenAlreadyActiveError) {
        setRegenerateDialogOpen(false);
        setRegenerateJobId(err.jobId);
        setUnconfirmedRunId(err.jobId);
        setRegenerateOutcome(null);  // the previous run's card must not outlive the run it describes
        setRegenerateProgress(err.progress);
        toast({
          title: 'Attached to the run already in progress',
          description: 'This site already had a real-time regeneration running, so nothing was '
            + 'started twice. Its progress is shown below.',
        });
        return;
      }
      console.error('[ContentReviewDashboard] Bulk regenerate error:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to bulk regenerate content',
        variant: 'destructive',
      });
    } finally {
      setBulkRegenerateLoading(false);
    }
  };

  // Count product_aeo items in selection
  const selectedProductAeoCount = Array.from(selectedItems).filter(key =>
    key.startsWith('product_aeo:')
  ).length;

  // Check if all visible items (on current page) are selected
  const allVisibleSelected = queue.length > 0 && queue.every(item =>
    selectedItems.has(`${item.contentType}:${item.id}`)
  );

  // Check if there are more items across pages
  const hasMoreItemsAcrossPages = pagination.total > queue.length;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Content Review Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Review and approve AI-generated content
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Metrics */}
      <ReviewMetrics metrics={metrics} loading={metricsLoading} />

      {/* Top Reviewers */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="md:col-span-2">
          {/* Filters */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {/* Search Input */}
                <div className="flex-1 min-w-[250px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Search Product
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by product name or SKU..."
                      value={searchInput}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="pl-9 pr-9"
                    />
                    {searchInput && (
                      <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* URL Filter — products only; matches a substring of the product URL */}
                <div className="flex-1 min-w-[250px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Filter by URL
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Paste a product URL or its embedded ID..."
                      value={urlInput}
                      onChange={(e) => handleUrlContainsChange(e.target.value)}
                      className="pl-9 pr-9"
                    />
                    {urlInput && (
                      <button
                        onClick={clearUrlContains}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Content Type Filter */}
                <div className="flex-1 min-w-[180px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Content Type
                  </label>
                  <Select
                    value={filters.contentType || 'all'}
                    onValueChange={handleContentTypeChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            {type.icon && <type.icon className="h-4 w-4" />}
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Status
                  </label>
                  <Select
                    value={filters.status || 'all'}
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* FAQ QA Filter */}
                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    FAQ QA Status
                  </label>
                  <Select
                    value={faqQaFilter}
                    onValueChange={handleFaqQaFilterChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select FAQ status" />
                    </SelectTrigger>
                    <SelectContent>
                      {FAQ_QA_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* task-8xq63qswed: the facets the data already counts — one select per dimension, counts under the
                    other active facets, the Approvable preset in one click. Values live in `filters` (see facetFilters.ts). */}
                {FACET_SELECTS.map((sel) => (
                  <div className="flex-1 min-w-[160px]" key={sel.key} data-testid={`facet-${sel.key}`}>
                    <label className="text-sm font-medium mb-1.5 block">{sel.label}</label>
                    <Select value={facetSelectValue(filters, sel.key)} onValueChange={(v) => handleFacetChange(sel.key, v)}>
                      <SelectTrigger>
                        <SelectValue placeholder={sel.label} />
                      </SelectTrigger>
                      <SelectContent>
                        {sel.options.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {labelWithCount(facetCounts, sel.key, option.value, option.label)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium mb-1.5 block">Preset</label>
                  <div className="flex gap-2">
                    <Button
                      variant={filters.preset === 'approvable' ? 'default' : 'outline'}
                      size="sm"
                      onClick={handleApprovableToggle}
                      data-testid="facet-approvable"
                      title="Validation passed, hard gate clear, 4 or more spec rows"
                    >
                      Approvable{facetCounts ? ` (${facetCounts.approvable})` : ''}
                    </Button>
                    {facetsAreActive(filters) && (
                      <Button variant="ghost" size="sm" onClick={handleClearFacets} data-testid="facet-clear">
                        Clear facets
                      </Button>
                    )}
                  </div>
                  {facetCounts && (
                    <p className="text-xs text-muted-foreground mt-1" data-testid="facet-summary">
                      {facetCounts.matching} of {facetCounts.total} match the active facets{facetCounts.nonProduct ? ` (${facetCounts.nonProduct} non-product rows are kept under any facet)` : ''}{facetCounts.truncated ? ' (counted on the first 250,000)' : ''}
                    </p>
                  )}
                  {facetCountsError && (
                    <p className="text-xs text-destructive mt-1" role="alert" data-testid="facet-counts-error">
                      Facet counts failed: {facetCountsError}
                      <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs" onClick={retryFacetCounts} data-testid="facet-counts-retry">
                        Retry
                      </Button>
                    </p>
                  )}
                </div>

                {/* Previous Rejection Filter */}
                <div className="flex-1 min-w-[170px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Rejection History
                  </label>
                  <Select
                    value={previousRejectionFilter}
                    onValueChange={handlePreviousRejectionFilterChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select rejection history" />
                    </SelectTrigger>
                    <SelectContent>
                      {PREVIOUS_REJECTION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Manual Review Filter */}
                <div className="flex-1 min-w-[170px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Review Status
                  </label>
                  <Select
                    value={manualReviewFilter}
                    onValueChange={handleManualReviewFilterChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select review status" />
                    </SelectTrigger>
                    <SelectContent>
                      {MANUAL_REVIEW_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category Filter */}
                <div className="flex-1 min-w-[180px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Category
                  </label>
                  <Select
                    value={categoryFilter}
                    onValueChange={handleCategoryFilterChange}
                    disabled={categoriesLoading || categories.length === 0}
                  >
                    <SelectTrigger>
                      <FolderTree className="h-4 w-4 mr-2 opacity-50" />
                      <SelectValue placeholder={categoriesLoading ? "Loading..." : "Select category"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.category} value={cat.category}>
                          <span title={cat.category}>
                            {getCategoryShortName(cat.category)} ({cat.productCount})
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Range Filter */}
                <div className="flex-1 min-w-[180px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Date Range
                  </label>
                  <Select
                    value={datePreset}
                    onValueChange={handleDatePresetChange}
                  >
                    <SelectTrigger>
                      <Calendar className="h-4 w-4 mr-2 opacity-50" />
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_PRESET_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Show Inactive Products Toggle */}
                <div className="flex items-center min-w-[180px] pt-6">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="show-inactive"
                      checked={showInactive}
                      onCheckedChange={handleShowInactiveChange}
                    />
                    <Label
                      htmlFor="show-inactive"
                      className="text-sm font-medium cursor-pointer flex items-center gap-1.5"
                    >
                      <Archive className="h-4 w-4 text-muted-foreground" />
                      Show Inactive
                    </Label>
                  </div>
                </div>

                {/* Show Published Toggle */}
                <div className="flex items-center min-w-[180px] pt-6">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="show-published"
                      checked={showPublished}
                      onCheckedChange={handleShowPublishedChange}
                    />
                    <Label
                      htmlFor="show-published"
                      className="text-sm font-medium cursor-pointer flex items-center gap-1.5"
                    >
                      <CheckCircle className="h-4 w-4 text-muted-foreground" />
                      Show Published
                    </Label>
                  </div>
                </div>

                {/* Custom Date Inputs - only show when custom is selected */}
                {datePreset === 'custom' && (
                  <div className="flex-1 min-w-[300px]">
                    <label className="text-sm font-medium mb-1.5 block">
                      Custom Range
                    </label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={format(dateRange.from, 'yyyy-MM-dd')}
                        onChange={(e) => {
                          const newDate = new Date(e.target.value);
                          if (!isNaN(newDate.getTime())) {
                            handleDateRangeChange({
                              from: startOfDay(newDate),
                              to: dateRange.to
                            });
                          }
                        }}
                        className="flex-1"
                      />
                      <span className="text-muted-foreground">to</span>
                      <Input
                        type="date"
                        value={format(dateRange.to, 'yyyy-MM-dd')}
                        onChange={(e) => {
                          const newDate = new Date(e.target.value);
                          if (!isNaN(newDate.getTime())) {
                            handleDateRangeChange({
                              from: dateRange.from,
                              to: newDate
                            });
                          }
                        }}
                        className="flex-1"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Active filters summary */}
              <div className="flex flex-wrap gap-2 mt-4">
                {filters.search && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                    Search: {filters.search}
                  </Badge>
                )}
                {filters.contentType && (
                  <Badge variant="secondary">
                    Type: {CONTENT_TYPES.find(t => t.value === filters.contentType)?.label}
                  </Badge>
                )}
                {filters.status && (
                  <Badge variant="secondary">
                    Status: {STATUS_OPTIONS.find(s => s.value === filters.status)?.label}
                  </Badge>
                )}
                {faqQaFilter !== 'all' && (
                  <Badge variant="secondary">
                    FAQ: {FAQ_QA_OPTIONS.find(o => o.value === faqQaFilter)?.label}
                  </Badge>
                )}
                {facetBadges(filters).map((label) => (
                  <Badge variant="secondary" key={label} className="bg-emerald-100 text-emerald-800">
                    {label}
                  </Badge>
                ))}
                {previousRejectionFilter !== 'all' && (
                  <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                    {PREVIOUS_REJECTION_OPTIONS.find(o => o.value === previousRejectionFilter)?.label}
                  </Badge>
                )}
                {manualReviewFilter !== 'all' && (
                  <Badge variant="secondary" className={manualReviewFilter === 'needs_review' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                    {MANUAL_REVIEW_OPTIONS.find(o => o.value === manualReviewFilter)?.label}
                  </Badge>
                )}
                {categoryFilter !== 'all' && (
                  <Badge variant="secondary" className="bg-purple-100 text-purple-800" title={categoryFilter}>
                    Category: {getCategoryShortName(categoryFilter)}
                  </Badge>
                )}
                {showInactive && (
                  <Badge variant="secondary" className="bg-gray-200 text-gray-700">
                    <Archive className="h-3 w-3 mr-1" />
                    Including Inactive
                  </Badge>
                )}
                <Badge variant="secondary">
                  {datePreset === 'custom'
                    ? `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`
                    : DATE_PRESET_OPTIONS.find(o => o.value === datePreset)?.label
                  }
                </Badge>
                {selectedSite && (
                  <Badge variant="outline">
                    Site: {selectedSite.name || selectedSite.url}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Reviewers */}
        <TopReviewers
          reviewers={metrics?.topReviewers || []}
          loading={metricsLoading}
        />
      </div>

      {/* Content Queue */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span>Content Queue</span>
              <Badge variant="secondary">{pagination.total} items</Badge>
              {selectedItems.size > 0 && (
                <Badge variant="outline">
                  {selectedItems.size} selected
                  {allItemsSelected && <span className="font-medium text-primary"> (all items)</span>}
                </Badge>
              )}
              {bulkSelectionScopeNotice(selectedItems.size, pagination.total, isSelectionCurrent(selectionScope, selectionScopeSignature(filters))) && (
                <Badge
                  variant="outline"
                  className="border-amber-500 text-amber-700 dark:text-amber-400"
                  title="A bulk action applies only to the selected rows. Rows in the current filter that are not selected will not be affected."
                >
                  {bulkSelectionScopeNotice(selectedItems.size, pagination.total, isSelectionCurrent(selectionScope, selectionScopeSignature(filters)))}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Select all items on page that don't need manual review */}
              {queue.some(item => !item.needsManualReview && item.status === 'pending') && !allVisibleSelected && (
                <Button
                  variant="outline"
                  onClick={handleSelectAllSafe}
                  disabled={bulkApproveLoading || loadingAllIds}
                  size="sm"
                >
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Select Safe Items
                </Button>
              )}

              {/* Select ALL items across pages - show when all visible are selected but more exist */}
              {allVisibleSelected && !allItemsSelected && hasMoreItemsAcrossPages && (
                <Button
                  variant="outline"
                  onClick={selectAllItemsAcrossPages}
                  disabled={loadingAllIds || bulkApproveLoading}
                  size="sm"
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  {loadingAllIds ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <CheckSquare className="h-4 w-4 mr-2" />
                      Select All {pagination.total} Items
                    </>
                  )}
                </Button>
              )}

              {/* Clear Selection */}
              {selectedItems.size > 0 && (
                <Button
                  variant="outline"
                  onClick={clearSelection}
                  disabled={bulkApproveLoading || bulkRegenerateLoading}
                  size="sm"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Clear Selection
                </Button>
              )}

              {selectedItems.size > 0 && (
                <>
                  <Button
                    onClick={handleBulkApprove}
                    disabled={bulkApproveLoading || bulkRegenerateLoading}
                    size="sm"
                  >
                    {bulkApproveLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckSquare className="h-4 w-4 mr-2" />
                        Approve Selected ({selectedItems.size})
                      </>
                    )}
                  </Button>
                  {selectedProductAeoCount > 0 && (
                    <Button
                      onClick={handleOpenRegenerateDialog}
                      // ⛔ NOT disabled by realtimeRunActive. This button only OPENS the dialog, and
                      // the dialog is where batch-vs-real-time is chosen — disabling it meant an
                      // active real-time run also removed the operator's way to reach BATCH, which
                      // the backend permits. The duplicate-cohort refusal lives at the action, where
                      // the mode is actually known; the dialog's submit is disabled there instead.
                      disabled={bulkApproveLoading || bulkRegenerateLoading}
                      variant="outline"
                      size="sm"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Regenerate ({selectedProductAeoCount})
                    </Button>
                  )}
                </>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ContentQueue
            key={`queue-${pagination.total}-${filters.category || 'all'}-${queue[0]?.id || 'empty'}`}
            items={queue}
            loading={queueLoading}
            onReview={handleReview}
            pagination={pagination}
            onPageChange={handlePageChange}
            selectedItems={selectedItems}
            onSelectionChange={handleSelectionChange}
            showSelection={true}
            sortBy={filters.sortBy as 'updated_at' | 'title' | 'content_type'}
            sortOrder={filters.sortOrder as 'ASC' | 'DESC'}
            onSortChange={handleSortChange}
          />
        </CardContent>
      </Card>

      {/* Review Panel */}
      <ReviewPanel
        open={reviewPanelOpen}
        onClose={handleReviewPanelClose}
        item={selectedItem}
        onActionComplete={handleActionComplete}
      />

      {/* ⛔ REAL-TIME REGEN PROGRESS — the visible half of the 504 fix.
          "No button = not delivered": without this the operator gets a 202 and no signal at all,
          which is the same blindness the 504 caused, just quieter. Rendered OUTSIDE the dialog on
          purpose — the dialog closes on submit, and a run that takes ~2 min per product must stay
          watchable after it closes. */}
      {(regenerateJobId || regenerateOutcome) && (
        <Card className="mb-4">
          <CardContent className="pt-4 space-y-2">
            {regenerateJobId && (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{regenerateProgress.message || 'Starting…'}</span>
                </div>
                <div className="h-2 w-full rounded bg-muted">
                  <div
                    className="h-2 rounded bg-primary transition-all"
                    style={{ width: `${Math.min(100, Math.max(0, regenerateProgress.percent))}%` }}
                  />
                </div>
              </>
            )}
            {regenerateOutcome && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{regenerateOutcome.message}</span>
                  <div className="flex items-center gap-2">
                    {/* ⛔ THE EXPLICIT OPERATOR OVERRIDE — the only release other than a confirmed
                        terminal state. It exists because lost tracking leaves the guard ON: without
                        a way out, an operator whose tracking dropped could never start another run
                        without a page reload, and a guard with no escape gets worked around rather
                        than obeyed. The label states the risk instead of hiding it. */}
                    {overrideReleasesGuard(regenerateOutcome.runId, unconfirmedRunId) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUnconfirmedRunId(null)}
                        title="The previous run may still be generating in the background. Starting another will regenerate those products again."
                      >
                        Allow a new run anyway
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setRegenerateOutcome(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {/* ⚠️ PER-PRODUCT FAILURES ARE SHOWN INDIVIDUALLY. A cohort where one product hit a
                    gate (e.g. thin extracted_attributes) is a COMPLETED run with a failure in it —
                    collapsing that to one verdict is what made the old response useless. */}
                {regenerateOutcome.details.filter(d => d.status !== 'success').map(d => (
                  <div key={d.productId} className="text-xs text-muted-foreground">
                    <Badge variant="outline" className="mr-2">{d.status}</Badge>
                    {d.productId}{(d.error || d.reason) ? ` — ${d.error || d.reason}` : ''}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bulk Regenerate Dialog */}
      <Dialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
        {/* max-h + scroll: the force options and the what-will-run summary made this dialog
            taller than the viewport on short screens, which pushed the footer — and therefore the
            Start button — off-screen with no way to reach it. A control that cannot be reached is
            the same failure as one that does not exist, which is what this PR is about. */}
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Regenerate Selected Products</DialogTitle>
            <DialogDescription>
              Regenerate AEO content for {selectedProductAeoCount} selected product{selectedProductAeoCount !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <RadioGroup
              value={regenerateMode}
              onValueChange={(value) => {
                const next = value as 'batch' | 'realtime';
                setRegenerateMode(next);
                // ⛔ Clear rescore when leaving batch. The service now THROWS on
                // realtime+forceRescore instead of silently coercing it, so a stale true here would
                // let the operator tick rescore in batch, switch to realtime, and hit an error they
                // did not cause. Making the service honest created this path; the UI has to close
                // it rather than rely on the disabled attribute alone.
                if (next === 'realtime') setForceRescore(false);
                // PR #5982 round 4: the completion outcome is applied when the RUN completes, and only Batch
                // creates a run — the server refuses a non-default outcome on Real-time (400
                // regen_outcome_unsupported_for_mode), so the choice is reset and disabled here.
                if (next === 'realtime') setRegenOutcome('leave_needs_review');
              }}
              className="space-y-3"
            >
              <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="batch" id="batch" className="mt-1" />
                <Label htmlFor="batch" className="flex-1 cursor-pointer">
                  <div className="font-medium">Batch Mode (Recommended)</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Uses batch processing for ~50% cost savings. Processing completes within ~24 hours.
                    Uses your configured LLM model from Content Settings.
                  </div>
                </Label>
              </div>

              <div className={`flex items-start space-x-3 p-3 border rounded-lg ${
                selectedProductAeoCount > 10
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-muted/50 cursor-pointer'
              }`}>
                <RadioGroupItem
                  value="realtime"
                  id="realtime"
                  className="mt-1"
                  disabled={selectedProductAeoCount > 10}
                />
                <Label
                  htmlFor="realtime"
                  className={`flex-1 ${selectedProductAeoCount > 10 ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="font-medium">Real-time Mode</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {selectedProductAeoCount > 10 ? (
                      <span className="text-amber-600">
                        Limited to 10 products to prevent timeout. Select fewer items or use batch mode.
                      </span>
                    ) : (
                      'Processes immediately. Best for small selections when you need results right away.'
                    )}
                  </div>
                </Label>
              </div>
            </RadioGroup>

            {regenerateMode === 'batch' && (
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  After starting, you can track progress on the Feed Import Status page.
                  Products will appear in the Content Queue once regeneration completes.
                </p>
              </div>
            )}

            {/* S3 — force flags. The backend has accepted these since 2026-05-12; this dialog
                never sent them, so both were permanently false from the UI. Verified honoured
                end-to-end before wiring. forceRescore is BATCH-ONLY: the route 400s on realtime
                (routes/content-review.js:2153-2163), so it is disabled with the reason shown
                rather than silently switching the operator's mode — a silent switch would run
                their work on a pipeline they did not choose. */}

            {/* WHAT WILL RUN — stated before the operator commits, naming the keys as they are
                PERSISTED (processing_options.*), not as the API spells them. Auditing a run row for
                "forceReEnrich" finds nothing and reads as "the flag was dropped"; naming the stored
                key here is what lets the operator and the next auditor confirm force actually
                applied. */}
            <div className="rounded-lg border border-dashed p-3 text-sm">
              <div className="font-medium">What will run</div>
              <ul className="mt-1 space-y-1 text-muted-foreground">
                <li>
                  AEO regeneration for {selectedProductAeoCount} product{selectedProductAeoCount !== 1 ? 's' : ''} via{' '}
                  <span className="font-medium">{regenerateMode === 'batch' ? 'batch' : 'real-time'}</span> mode.
                </li>
                <li>
                  {forceReEnrich
                    ? <>AI enrichment re-runs first, cache bypassed <code>(force_ai_enrichment=true)</code>.</>
                    : <>Enrichment is <span className="font-medium">skipped where cached</span> <code>(force_ai_enrichment=false)</code>.</>}
                </li>
                <li>
                  {regenerateMode === 'batch' && forceRescore
                    ? <>PBS scoring re-runs for the whole cohort <code>(force_rescore=true)</code>. If any product fails to score, the sub-batch is refused rather than regenerating on stale scores.</>
                    : <>PBS scoring is <span className="font-medium">not re-run</span> <code>(force_rescore=false)</code>.</>}
                </li>
              </ul>
            </div>
            <div className="space-y-3 rounded-lg border p-3">
              <div className="text-sm font-medium">Force options</div>

              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={forceReEnrich}
                  onChange={(e) => setForceReEnrich(e.target.checked)}
                />
                <span className="flex-1">
                  <span className="text-sm font-medium">Force re-enrichment</span>
                  <span className="block text-sm text-muted-foreground">
                    Re-runs AI enrichment before regenerating, bypassing the enrichment cache.
                    Stored as <code>processing_options.force_ai_enrichment</code>.
                  </span>
                </span>
              </label>

              <label
                className={`flex items-start space-x-3 ${
                  regenerateMode === 'realtime' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={forceRescore && regenerateMode === 'batch'}
                  disabled={regenerateMode === 'realtime'}
                  onChange={(e) => setForceRescore(e.target.checked)}
                />
                <span className="flex-1">
                  <span className="text-sm font-medium">Force re-scoring (PBS)</span>
                  <span className="block text-sm text-muted-foreground">
                    {regenerateMode === 'realtime'
                      ? 'Batch-only — real-time mode does not re-run PBS scoring. Switch to Batch Mode to use this.'
                      : 'Re-runs benefit scoring for the whole cohort. If any product fails to score, the sub-batch is refused rather than regenerating against stale scores. Stored as processing_options.force_rescore.'}
                  </span>
                </span>
              </label>
            </div>

            {/* task-fzfuulrr28: completion outcome. Applied by the sweeper when the run completes, through
                the same approval path as a manual click (same audit fields), never at dispatch. Pages the
                validator refused are never auto-approved and are counted on the run's summary. */}
            <div className={`space-y-2 pt-2 border-t ${regenerateMode === 'realtime' ? 'opacity-50' : ''}`}>
              <div className="text-sm font-medium">When the run completes</div>
              {regenerateMode === 'realtime' && (
                <div className="text-sm text-muted-foreground">
                  Batch-only — real-time regenerates in the request and creates no run for the sweeper to complete,
                  so pages come back as needs review. Switch to Batch Mode to choose an outcome.
                </div>
              )}
              <RadioGroup
                value={regenOutcome}
                onValueChange={(value) => setRegenOutcome(value as RegenOutcome)}
                className="space-y-2"
                disabled={regenerateMode === 'realtime'}
              >
                <div className="flex items-start space-x-3 p-2 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="leave_needs_review" id="outcome-leave" className="mt-1" />
                  <Label htmlFor="outcome-leave" className="flex-1 cursor-pointer">
                    <div className="font-medium">{REGEN_OUTCOME_LABELS.leave_needs_review} (default)</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Every regenerated page comes back as needs review and is approved by hand.
                    </div>
                  </Label>
                </div>
                <div className="flex items-start space-x-3 p-2 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="approve_previously_approved" id="outcome-previous" className="mt-1" />
                  <Label htmlFor="outcome-previous" className="flex-1 cursor-pointer">
                    <div className="font-medium">{REGEN_OUTCOME_LABELS.approve_previously_approved}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Pages that were approved before this run are re-approved on completion; pages that were not stay as needs review.
                    </div>
                  </Label>
                </div>
                <div className="flex items-start space-x-3 p-2 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="approve_all" id="outcome-all" className="mt-1" />
                  <Label htmlFor="outcome-all" className="flex-1 cursor-pointer">
                    <div className="font-medium">{REGEN_OUTCOME_LABELS.approve_all}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Every page this run generates is approved on completion. Pages the validator refuses are never auto-approved; the run summary reports them.
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRegenerateDialogOpen(false)}
              disabled={bulkRegenerateLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkRegenerate}
              // ⛔ The duplicate-cohort refusal belongs HERE, not on the button that opens this
              // dialog: this is the first point where the MODE is known. Batch stays available
              // during a real-time run, which is what the backend slot actually permits.
              disabled={bulkRegenerateLoading || (realtimeRunActive && regenerateMode === 'realtime')}
              title={realtimeRunActive && regenerateMode === 'realtime'
                ? 'A real-time regeneration is already running for this site. Batch mode is still available.'
                : undefined}
            >
              {bulkRegenerateLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {regenerateMode === 'batch' ? 'Starting...' : 'Regenerating...'}
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {regenerateMode === 'batch' ? 'Start Batch' : 'Regenerate Now'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
